/**
 * @fileoverview Acciones de sesión del módulo user contra CR Auth: login, renovación y logout.
 * @module store/modules/user/session.actions
 */

import authIntegration from '../../../integrations/auth.integration'
import environment from '../../../utils/environment'
import { AUTH_ERROR_CODES } from '../../../integrations/models/auth.models'
import {
  toDocumentType,
  toRecaptchaToken,
  toSession,
  toLegacyLoginResponse,
  toLegacyLoginError,
  toForcedResetError
} from '../../../interfaces/authMappers'

/** Margen antes del vencimiento del access token en el que se renueva por adelantado. */
export const ACCESS_TOKEN_MARGIN_MS = 60 * 1000

/** Renovación en curso: las llamadas concurrentes esperan la misma promesa. */
let refreshInFlight = null

/**
 * Error de sesión inexistente o vencida.
 * @param {string} code
 * @returns {Error}
 */
function sessionError (code) {
  const error = new Error('La sesión no está disponible')
  error.name = 'SessionError'
  error.code = code
  error.status = 401
  return error
}

/**
 * Renueva la sesión una sola vez aunque haya llamadas concurrentes.
 * Si CR Auth rechaza el refresh token (401), la sesión vence; un error de red no la borra.
 * @param {Function} commit
 * @param {string} refreshToken
 * @returns {Promise<Object>} Sesión renovada
 */
function refreshSession (commit, refreshToken) {
  if (!refreshInFlight) {
    refreshInFlight = authIntegration.refresh(refreshToken)
      .then(tokens => {
        const session = toSession(tokens, Date.now())
        commit('setSession', session)
        refreshInFlight = null
        return session
      }, error => {
        refreshInFlight = null
        if (error && error.status === 401) {
          commit('expireSession')
        }
        throw error
      })
  }
  return refreshInFlight
}

export default {
  /**
   * Inicia sesión en CR Auth y deja la sesión con la forma que ya usa el front.
   * Rechaza con `{ status, data }` en la forma de error que maneja `login.mixin`.
   * @param {Object} context
   * @param {{tipoDocumento: number, numeroDocumento: string, clave: string, token: string, saveData: boolean}} form
   * @returns {Promise<{data: Object}>}
   */
  login ({ commit }, form) {
    const request = {
      documentType: toDocumentType(form.tipoDocumento),
      documentNumber: (form.numeroDocumento || '').trim(),
      password: form.clave,
      recaptchaToken: toRecaptchaToken(form.token, environment.isDev())
    }

    return authIntegration.login(request)
      .catch(error => { throw toLegacyLoginError(error) })
      .then(result => {
        if (result.mustChangePassword) {
          // La clave es un código temporal del sistema anterior: se cierra la sesión y se pide recuperar.
          return authIntegration.logout(result.accessToken)
            .catch(() => undefined)
            .then(() => { throw toForcedResetError() })
        }

        const session = toSession(result, Date.now())
        const legacyResponse = toLegacyLoginResponse(result, session)
        commit('setCodeUser', legacyResponse.codigoUsuario)
        commit('setFunctions', legacyResponse.funcionalidades)
        if (form.saveData) {
          commit('setUser', { data: legacyResponse })
        }
        commit('setSession', session)
        return { data: legacyResponse }
      })
  },

  /**
   * Garantiza un access token vigente: si le queda menos de un minuto, lo renueva con el refresh token.
   * @param {Object} context
   * @param {{force: boolean}} [options] - force renueva aunque el token siga vigente (tras un 401)
   * @returns {Promise<Object>} Sesión vigente
   */
  ensureSession ({ state, commit }, options) {
    const force = !!(options && options.force)
    const session = state.session
    const now = Date.now()

    if (!session || !session.refreshToken) {
      if (state.user) {
        // Sesión del login anterior (sin refresh token): debe volver a ingresar.
        commit('expireSession')
      }
      return Promise.reject(sessionError('NO_SESSION'))
    }
    if (!force && session.accessExpiresAt - now > ACCESS_TOKEN_MARGIN_MS) {
      return Promise.resolve(session)
    }
    if (session.refreshExpiresAt <= now) {
      commit('expireSession')
      return Promise.reject(sessionError(AUTH_ERROR_CODES.AUTH_REFRESH_TOKEN_INVALID))
    }
    return refreshSession(commit, session.refreshToken)
  },

  /**
   * Compatibilidad con las llamadas existentes: nunca rechaza, igual que el checkJWT anterior.
   * @returns {Promise<void>}
   */
  checkJWT ({ dispatch }) {
    return dispatch('ensureSession').then(() => undefined, () => undefined)
  },

  /**
   * Usado por el setInterval de Protected.vue: resuelve con `codigoRespuesta` '01' si la sesión sigue vigente.
   * @returns {Promise<{data: {codigoRespuesta: string}}>}
   */
  tmpCheckJWT ({ dispatch }) {
    return dispatch('ensureSession').then(() => ({ data: { codigoRespuesta: '01' } }))
  },

  /**
   * Cierra la sesión en CR Auth. Nunca rechaza: la limpieza local se hace siempre.
   * Si el access token ya venció, se renueva solo para poder revocar la sesión
   * (sin pasar por ensureSession, que avisaría al usuario de una sesión vencida).
   * @returns {Promise<void>}
   */
  inactiveJwtLogout ({ state }) {
    const session = state.session
    if (!session) {
      return Promise.resolve()
    }
    const accessToken = session.accessExpiresAt - Date.now() > ACCESS_TOKEN_MARGIN_MS
      ? Promise.resolve(session.accessToken)
      : authIntegration.refresh(session.refreshToken).then(tokens => tokens.accessToken)
    return accessToken
      .then(token => authIntegration.logout(token))
      .then(() => undefined, () => undefined)
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/user/session.actions.js